import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcPortfolioV1HoldQueryServiceHoldGet } from '@interxfi/api-client/generated/hooks/ixc-portfolio-v1-hold-query-service-hold-get';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import { PortfolioHoldEntityMenu } from '../../../../../../../../../../features/portfolio/hold/portfolio-hold-entity-menu';
import { HoldEventSummary } from '../../../../../../../../../../features/portfolio/hold/portfolio-hold-event';
import { getPortfolioHoldDetailPageListItems } from '../../../../../../../../../../features/portfolio/hold/portfolio-hold-list-items';
import { getPortfolioHoldDetailPath } from '../../../../../../../../../../features/portfolio/hold/portfolio-hold-routing';
import { PortfolioHoldStatusPill } from '../../../../../../../../../../features/portfolio/hold/portfolio-hold-status-pill';
import { PortfolioHoldSummaryBar } from '../../../../../../../../../../features/portfolio/hold/portfolio-hold-summary-bar';
import { RegistrationAccountPageHeader } from '../../../../../../../../../../features/registration/account/registration-account-page-header';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/hold/_hold/$holdId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-hold-${params.holdId}`,
        label: 'Hold',
        to: getPortfolioHoldDetailPath(params.accountId, params.holdId),
        suffix: <CopyButton content={params.holdId} label="Copy Hold ID" tooltip={{ copy: params.holdId, copied: 'Copied Hold ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Hold'),
  component: AccountHoldDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load hold', 'view this hold');

function AccountHoldDetail() {
  const { accountId, holdId } = Route.useParams();
  const request = { accountId, holdId } as const;
  const { data, error, isPending } = useIxcPortfolioV1HoldQueryServiceHoldGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getPortfolioHoldDetailPageListItems(data?.hold), [data?.hold]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <RegistrationAccountPageHeader
          accountId={accountId}
          actions={<PortfolioHoldEntityMenu accountId={accountId} holdId={holdId} />}
          pills={<PortfolioHoldStatusPill isPending={showPending} size="large" status={data?.hold?.status} />}
        />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <PortfolioHoldSummaryBar {...request} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <HoldEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
